import { FLOW_QUERY_KEY, useGetOneFlow } from "#hooks/adapters/useFlows";
import { useNavigate, useParams, useSearchParams } from "#routers/hooks";
import EditFlowDialog from "#src/batteries-included-components/Dialogs/EditFlowDialog";
import { UpdateEntityStatusDialog } from "#src/batteries-included-components/Dialogs/UpdateEntityStatusDialog";
import { FlowsViewFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { PageErrorContent } from "#src/batteries-included-components/Layouts/Errors/PageError";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useUpdateAssetStatusAsync } from "#src/components/hooks/adapters/useAssets";
import { useListWorkflows } from "#src/components/hooks/adapters/useWorkflows";
import { useCreateEditMetaSegments } from "#src/components/hooks/useCreateEditMetaSegments";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { AssociatedFormSubmissionsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedFormSubmissionsTab";
import { getAssetFilters } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflows.helpers";
import { AssociatedWorkflowsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflowsTab";
import { hydrateDateRange } from "#src/utils/date";
import { useBreadcrumbsFromRoute } from "#utils/route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DropdownMenu,
  generateDocumentTitle,
  Icon,
  Page,
  Pill,
  Tab,
  useToast,
} from "@validereinc/common-components";
import {
  FlowAdapter,
  FormSubmissionAdapter,
  isResponseErrorType,
  ResourceDefinitions,
  Resources,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  HTTPResponseError,
  toTitleCaseString,
} from "@validereinc/utilities";
import React, { useEffect, useState } from "react";
import { Titled } from "react-titled";
import { FlowListRoute } from "../../index";
import { ChangeLogTab } from "./ChangeLogTab";
import { FlowEstimationMethodsTab } from "./FlowEstimationMethodsTab";
import { FlowOverviewTab } from "./FlowOverviewTab/FlowOverviewTab";
import { FlowRecordsTab } from "./FlowRecordsTab";
import { FlowDetailRoute } from "./index";

const DEFAULT_TAB_KEY = "overview";

export const FlowDetailPageContent = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdateFlowStatusDialogOpen, setIsUpdateFlowStatusDialogOpen] =
    useState(false);
  const [areFormsEnabled, isLoadingAreFormsEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:forms",
    permissionQuery: {
      $and: [
        "form_categories:read",
        "form_schemas:read",
        "form_submissions:read",
      ],
    },
  });
  const [areWorkflowsEnabled, isLoadingAreWorkflowsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:workflows",
      permissionQuery: {
        $and: ["workflows:read", "workflow_templates:read"],
      },
    });
  const [isChangeLogAvailable, isLoadingIsChangeLogAvailable] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:activity_logs",
      permissionQuery: "activities:read",
    });
  const [areEstimationMethodsEnabled, isLoadingAreEstimationMethodsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:volumetric_estimation_methods",
    });
  const { localize, isLoading: isLoadingLocalization } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const { toast } = useToast();
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const [assetsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "assets-shared"
  );
  const [viewFilters] = useStickyState<{
    period: { from: string; to: string };
  }>(
    {} as { period: { from: string; to: string } },
    assetsSharedViewConfigStorageKey
  );
  const {
    data,
    isLoading: isLoadingFlowDetails,
    error,
  } = useGetOneFlow({
    id: flowId,
    meta: {
      period: getYearMonthFromDateRange(
        viewFilters.period
          ? hydrateDateRange(viewFilters.period)
          : DEFAULT_DATE_RANGES.currentWholeMonth
      ),
    },
  });
  const flow = data?.data;
  const createEditMetaSegments = useCreateEditMetaSegments({
    editedAt: flow?.updated_at,
    createdAt: flow?.created_at,
  });
  const [breadcrumbs] = useBreadcrumbsFromRoute(FlowDetailRoute, {
    detail: { title: flow?.name },
  });
  const relatedFormSubmissionsQuery = useQuery({
    queryKey: [Resources.FORM_SUBMISSION, Resources.FLOW, flowId],
    queryFn: () => {
      return FormSubmissionAdapter.getList({
        filters: {
          related_entity_id: flowId,
        },
      });
    },
    enabled: areFormsEnabled && !!flowId,
  });
  const relatedWorkflowsQuery = useListWorkflows(
    {
      page: 1,
      pageSize: 1,
      filters: {
        ...getAssetFilters(Resources.FLOW, { assetId: flowId }),
      },
    },
    {
      enabled: areWorkflowsEnabled && !!flowId,
    }
  );
  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof FlowAdapter.deleteOne>[0]) => {
      return FlowAdapter.deleteOne(params);
    },
    onSuccess: (_, params) => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${localize(Resources.FLOW)}${params.previousData?.name ? ` ${params.previousData?.name}.` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: [FLOW_QUERY_KEY] });
      navigate(FlowListRoute.toLinkParts());
    },
    onError: (error) => {
      if (
        error instanceof HTTPResponseError &&
        error.responseBodyJson &&
        isResponseErrorType(error.responseBodyJson)
      ) {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.FLOW)} due to a server error: ${error.responseBodyJson.error}`,
        });
      } else {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.FLOW)}. An unknown error occurred.`,
        });
      }
    },
  });

  const [updateFlowStatusJobStorageKey] = useStorageKey(
    ["async-job"],
    `update-flow-status-${flowId}`
  );

  const updateStatusMutation = useUpdateAssetStatusAsync({
    jobKey: updateFlowStatusJobStorageKey,
    assetId: flowId,
    setIsUpdating: setIsUpdatingStatus,
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [
          FLOW_QUERY_KEY,
          {
            flowId,
            period: getYearMonthFromDateRange(
              viewFilters.period
                ? hydrateDateRange(viewFilters.period)
                : DEFAULT_DATE_RANGES.currentWholeMonth
            ),
          },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.ACTIVITY],
      });
    },
  });
  const handleChangeFlowStatusClick = () =>
    setIsUpdateFlowStatusDialogOpen(true);

  const onActiveTabKeyChange = (tabKey: string) => {
    setSearchParams({ tab: tabKey });
  };

  useEffect(() => {
    if (!searchParams?.tab) {
      onActiveTabKeyChange("overview");
    }
  }, []);

  const hasAssociatedResources =
    !!relatedWorkflowsQuery.data?.total_entries ||
    !!relatedFormSubmissionsQuery.data?.total_entries;
  const activePeriod: string =
    getYearMonthFromDateRange(
      viewFilters?.period
        ? hydrateDateRange(viewFilters.period)
        : DEFAULT_DATE_RANGES.currentWholeMonth
    ) ?? "";

  return (
    <>
      <Page
        breadcrumbs={breadcrumbs}
        title={
          flow ? (
            <>
              {flow?.name}{" "}
              <Pill
                variant={flow?.status === "active" ? "success" : "default"}
                hasDot={false}
              >
                {toTitleCaseString(flow?.status ?? "-", {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            FlowDetailRoute.title
          )
        }
        category={localize("Flow")}
        activeTabKey={
          !isChangeLogAvailable && searchParams?.tab === "change-log"
            ? DEFAULT_TAB_KEY
            : (searchParams?.tab ?? DEFAULT_TAB_KEY)
        }
        onActiveTabKeyChange={onActiveTabKeyChange}
        isLoading={
          isLoadingFlowDetails ||
          isLoadingLocalization ||
          isLoadingAreEstimationMethodsEnabled ||
          isLoadingIsChangeLogAvailable ||
          isLoadingAreFormsEnabled ||
          isLoadingAreWorkflowsEnabled
        }
        actionRow={[
          <IsFeatureAvailable
            key="edit-access-policy"
            featureFlagQuery="core:flows"
            permissionQuery="flows:write"
            fallbackChildren={null}
          >
            <Button
              key="edit-flow-action"
              onClick={() => setAssetIdToEdit(flowId)}
            >
              Edit
            </Button>
          </IsFeatureAvailable>,
          <>
            <IsFeatureAvailable
              key="change-status-access-policy"
              featureFlagQuery="core:flows"
              permissionQuery="flows:write"
              fallbackChildren={null}
            >
              <Button
                isLoading={isUpdatingStatus}
                onClick={handleChangeFlowStatusClick}
              >
                Change Status
              </Button>
            </IsFeatureAvailable>
          </>,
          <IsFeatureAvailable
            key="more-access-policy"
            featureFlagQuery="core:flows"
            permissionQuery="flows:delete"
            fallbackChildren={null}
          >
            <DropdownMenu
              key="more"
              options={[
                {
                  label: "Delete",
                  variant: "danger",
                  isDisabled: deleteMutation.isLoading,
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      {...iconProps}
                      variant="trash"
                    />
                  ),
                  onClick: () => setIsDeleteConfirmationOpen(true),
                },
              ]}
            >
              <Button
                icon="dots-three-vertical"
                variant="outline"
              />
            </DropdownMenu>
          </IsFeatureAvailable>,
        ]}
        error={error}
        onErrorRender={({ error }) => <PageErrorContent error={error} />}
        renderMeta={
          createEditMetaSegments.length === 0
            ? undefined
            : ({ MetaSegments }) => (
                <MetaSegments values={createEditMetaSegments} />
              )
        }
      >
        <Tab
          title="Overview"
          tabKey="overview"
        >
          <Titled
            title={generateDocumentTitle(
              breadcrumbs.at(-1)?.title ?? FlowDetailRoute.title,
              breadcrumbs.at(-2)?.title
            )}
          >
            <FlowOverviewTab
              isLoading={isLoadingFlowDetails}
              flow={flow}
              viewConfigStorageKey={assetsSharedViewConfigStorageKey}
              viewFilterAreaSlot={
                <FlowsViewFilterArea
                  viewConfigStorageKey={assetsSharedViewConfigStorageKey}
                />
              }
            />
          </Titled>
        </Tab>
        <Tab
          title={ResourceDefinitions.record.label.plural}
          tabKey="records"
        >
          <Titled
            title={generateDocumentTitle(
              `${breadcrumbs.at(-1)?.title ?? localize(Resources.FLOW)} ${ResourceDefinitions.record.label.plural}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            <FlowRecordsTab flowId={flowId} />
          </Titled>
        </Tab>
        {areEstimationMethodsEnabled ? (
          <Tab
            title={ResourceDefinitions.estimation_method.label.plural}
            tabKey="estimation_methods"
          >
            <Titled
              title={generateDocumentTitle(
                `${breadcrumbs.at(-1)?.title ?? localize(Resources.FLOW)} ${ResourceDefinitions.estimation_method.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <FlowEstimationMethodsTab />
            </Titled>
          </Tab>
        ) : null}
        {areFormsEnabled ? (
          <Tab
            tabKey={Resources.FORM_SUBMISSION}
            title="Forms"
          >
            <Titled
              title={generateDocumentTitle(
                `${breadcrumbs.at(-1)?.title ?? localize(Resources.FLOW)} ${ResourceDefinitions.form_submission.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <AssociatedFormSubmissionsTab
                resourceId={flowId}
                resourceType={Resources.FLOW}
                defaultFormCategoryName={searchParams.form_category_name}
              />
            </Titled>
          </Tab>
        ) : null}
        {areWorkflowsEnabled ? (
          <Tab
            tabKey={Resources.WORKFLOW}
            title={ResourceDefinitions.workflow.label.plural}
          >
            <Titled
              title={generateDocumentTitle(
                `${breadcrumbs.at(-1)?.title ?? localize(Resources.FLOW)} ${ResourceDefinitions.workflow.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <AssociatedWorkflowsTab
                resourceId={flowId}
                resourceType={Resources.FLOW}
              />
            </Titled>
          </Tab>
        ) : null}
        <Tab
          tabKey="change-log"
          title={ResourceDefinitions.activity.label.singular}
          isDisabled={!isChangeLogAvailable}
          iconVariant={!isChangeLogAvailable ? "lock" : undefined}
        >
          <Titled
            title={generateDocumentTitle(
              `${breadcrumbs.at(-1)?.title ?? localize(Resources.FLOW)} ${ResourceDefinitions.activity.label.plural}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            {isChangeLogAvailable ? <ChangeLogTab resourceId={flowId} /> : null}
          </Titled>
        </Tab>
      </Page>

      <Dialog
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title={`Delete ${toTitleCaseString(localize(Resources.FLOW), { intent: "ui" })}`}
        actionRow={[
          <Button
            key="confirm"
            variant="error"
            onClick={() =>
              deleteMutation.mutate({
                id: flowId,
                previousData: flow,
              })
            }
            isLoading={deleteMutation.isLoading}
            disabled={hasAssociatedResources}
          >
            Delete
          </Button>,
        ]}
      >
        {hasAssociatedResources ? (
          <>
            You cannot delete {flow?.name} until the following associated
            resources are deleted:
            <ul style={{ listStyle: "none", padding: 0, marginBlock: 8 }}>
              {relatedWorkflowsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={FlowDetailRoute.toLinkParts({
                      pathParams: { flowId },
                      queryParams: { tab: Resources.WORKFLOW },
                    })}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedWorkflowsQuery.data?.total_entries} Workflows
                  </RoutingLink>
                </li>
              ) : null}
              {relatedFormSubmissionsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={FlowDetailRoute.toLinkParts({
                      pathParams: { flowId },
                      queryParams: { tab: Resources.FORM_SUBMISSION },
                    })}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedFormSubmissionsQuery.data?.total_entries} Form
                    Submissions
                  </RoutingLink>
                </li>
              ) : null}
            </ul>
            Please delete them and return here to delete this{" "}
            {localize(Resources.FLOW)}.
          </>
        ) : (
          <>
            Are you sure you want to delete this {localize(Resources.FLOW)}?
            This will also delete its associated Estimation Methods and Records.
            This action cannot be undone.
          </>
        )}
      </Dialog>
      <EditFlowDialog
        flowId={assetIdToEdit}
        onClose={() => setAssetIdToEdit(undefined)}
        defaultPeriod={activePeriod}
      />
      <UpdateEntityStatusDialog
        isOpen={isUpdateFlowStatusDialogOpen}
        onClose={() => {
          setIsUpdateFlowStatusDialogOpen(false);
        }}
        onSubmit={updateStatusMutation.handleUpdateAssetStatusAsync}
        itemCount={1}
        subject={{
          plural: ResourceDefinitions.flow.label.plural,
          singular: ResourceDefinitions.flow.label.singular,
        }}
        warningMessage="This will inactivate all associated estimation methods. Inactivating estimation methods will delete any associated results for the provided time range."
      />
    </>
  );
};

export const FlowDetailPage = () => <FlowDetailPageContent />;
